import type { AiCall } from "@pax/api-contract/ai/calls/v1alpha1";
import type { GameId } from "@pax/api-contract/common";
import type { GameSpan } from "@pax/api-contract/traces/v1alpha1";
import { clientFetch } from "@pax/telemetry/client";
import { type CommittedDelta, parseWorldArtifact } from "@pax/world";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, isNotFound } from "@/lib/api";

/** What the debugger reads: a game with everything it recorded, or a stored world file alone. */
export type GameRecords = { kind: "game"; gameId: string } | { kind: "world"; url: string };

/** One delta per line, as the routes that serve a game's committed deltas send them. */
async function readDeltaRecord(url: string): Promise<CommittedDelta[]> {
  const response = await clientFetch(url, { credentials: "include" });
  if (!response.ok) throw new Error("The game's deltas could not be read.");
  return (await response.text())
    .split("\n")
    .filter(line => line.trim())
    .map(line => JSON.parse(line) as CommittedDelta);
}

/** A stored world file: a header line, then one delta per line. */
async function readWorldFile(url: string): Promise<CommittedDelta[]> {
  const response = await clientFetch(url, { credentials: "include" });
  if (!response.ok) throw new Error("The world could not be read.");
  return parseWorldArtifact(await response.text());
}

/** The deltas of whatever the debugger reads. */
export function useWorldRecord(records: GameRecords) {
  const game = useGameRecord(records.kind === "game" ? records.gameId : null);
  const world = useQuery({
    queryKey: ["world-file", records.kind === "world" ? records.url : null],
    enabled: records.kind === "world",
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: () => readWorldFile(records.kind === "world" ? records.url : ""),
  });
  return records.kind === "game" ? game : world;
}

/**
 * Every delta the game committed, read once. A refresh asks only for the deltas after the last one
 * it has.
 */
function useGameRecord(gameId: string | null) {
  const queryClient = useQueryClient();
  const queryKey = ["game-record", gameId];
  return useQuery({
    queryKey,
    enabled: !!gameId,
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: async () => {
      const known = queryClient.getQueryData<CommittedDelta[]>(queryKey) ?? [];
      const after = known.at(-1)?.revision ?? 0;
      const added = await readDeltaRecord(
        `/api/traces/v1alpha1/games/${gameId}/deltas?after=${after}`,
      );
      return added.length ? [...known, ...added] : known;
    },
  });
}

/** More spans than the debugger lays out at once; past it, the oldest are left unread. */
const SPAN_CEILING = 50_000;

/** A game's spans, oldest first, read a page at a time from the newest. */
export function useGameSpans(gameId: string | null) {
  return useQuery({
    queryKey: ["trace-spans", gameId],
    enabled: !!gameId,
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: async () => {
      const newestFirst: GameSpan[] = [];
      let before: string | undefined;
      do {
        const page = await api.traces.games.spans.list({
          params: { gameId: gameId ?? "" },
          query: before ? { before } : {},
        });
        newestFirst.push(...page.spans);
        before = page.older ?? undefined;
      } while (before && newestFirst.length < SPAN_CEILING);
      return { spans: newestFirst.reverse(), oldestMissing: before !== undefined };
    },
  });
}

/** A game's model calls, oldest first, read a page at a time from the newest. */
export function useGameModelCalls(gameId: string | null) {
  return useQuery({
    queryKey: ["ai-calls", gameId],
    enabled: !!gameId,
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: async () => {
      const newestFirst: AiCall[] = [];
      let cursor: string | undefined;
      do {
        const page = await api.ai.calls.list({
          query: { gameId: gameId as GameId, limit: 100, cursor },
        });
        newestFirst.push(...page.items);
        cursor = page.nextCursor ?? undefined;
      } while (cursor);
      return newestFirst.reverse();
    },
  });
}

/** One span's whole stored row, or null when ClickHouse no longer has it. */
export function useSpanRow(gameId: string, spanId: string | undefined) {
  return useQuery({
    queryKey: ["trace-span-row", gameId, spanId],
    enabled: !!spanId,
    staleTime: Number.POSITIVE_INFINITY,
    retry: (count, error) => !isNotFound(error) && count < 2,
    queryFn: async () => {
      try {
        return (await api.traces.games.spans.get({ params: { gameId, spanId: spanId ?? "" } })).row;
      } catch (error) {
        if (isNotFound(error)) return null;
        throw error;
      }
    },
  });
}

/** One model call with both bodies, or null when ClickHouse no longer has it. */
export function useModelCall(callId: string | undefined) {
  return useQuery({
    queryKey: ["ai-call", callId],
    enabled: !!callId,
    staleTime: Number.POSITIVE_INFINITY,
    retry: (count, error) => !isNotFound(error) && count < 2,
    queryFn: async () => {
      try {
        return await api.ai.calls.get({ params: { callId: callId ?? "" } });
      } catch (error) {
        if (isNotFound(error)) return null;
        throw error;
      }
    },
  });
}
